import { getSessionOpenUrl } from '@/lib/gift-agent';
import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useInterval } from '@/hooks/useInterval';
import { useSessionPageStore } from '@/stores/sessionPageStore';
import {
  ExternalLink,
  Copy,
  Check,
  Eye,
  EyeOff,
  Square,
  Play,
  Trash2,
  RotateCcw,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

const RUNNER_LABEL_KEYS: Record<string, string> = {
  'claude-code': 'claude-code',
  'gift-ui': 'gift-ui',
  'acpx': 'acpx',
  'acpx-experimental': 'acpx-experimental',
};

export default function SessionPage() {
  const { t, i18n } = useTranslation(['session', 'common']);
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [showUpdateDetails, setShowUpdateDetails] = useState(false);
  const {
    session,
    project,
    accessInfo,
    loading,
    showPassword,
    copiedField,
    actionLoading,
    confirmAction,
    showAdvanced,
    setSession,
    setProject,
    setAccessInfo,
    setLoading,
    setShowPassword,
    setCopiedField,
    setActionLoading,
    setConfirmAction,
    setShowAdvanced,
    resetForSession,
  } = useSessionPageStore();
  const isGiftAgentSession = session?.runnerType === 'acpx' || session?.runnerType === 'acpx-experimental';

  useEffect(() => {
    resetForSession();
  }, [sessionId, resetForSession]);

  const fetchSession = useCallback(async () => {
    try {
      const sessionResponse = await fetch(`/api/sessions/${sessionId}`, { credentials: 'include' });

      if (sessionResponse.ok) {
        const data = await sessionResponse.json();
        setSession(data.data);

        if (data.data.projectId) {
          const projectResponse = await fetch(`/api/projects/${data.data.projectId}`, {
            credentials: 'include',
          });
          if (projectResponse.ok) {
            const projectData = await projectResponse.json();
            setProject(projectData.data);
          }
        }

        if (data.data.status === 'running') {
          const accessResponse = await fetch(`/api/sessions/${sessionId}/access`, {
            credentials: 'include',
          });
          if (accessResponse.ok) {
            const accessData = await accessResponse.json();
            setAccessInfo(accessData.data);
          }
        } else {
          setAccessInfo(null);
        }
      } else {
        navigate('/sessions');
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
      toast.error(t('session:errors.load'));
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate, setAccessInfo, setLoading, setProject, setSession, t]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const sessionNeedsAccessRefresh = Boolean(
    session
    && isGiftAgentSession
    && session.status === 'running'
    && !accessInfo,
  );

  useInterval(() => {
    void fetchSession();
  }, sessionNeedsAccessRefresh ? 3000 : null);

  const pollStatus = useCallback(async () => {
    if (!session || !session.containerId) return;

    try {
      const response = await fetch(`/api/sessions/${sessionId}/status`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        const nextSessionStatus = String(data?.data?.sessionStatus || '');
        const nextContainerStatus = String(data?.data?.status || '');
        const currentSessionStatus = String(session.status || '');

        if (nextSessionStatus === 'stopped' && currentSessionStatus !== 'stopped') {
          await fetchSession();
          return;
        }

        if (isGiftAgentSession && currentSessionStatus === 'running' && !accessInfo && nextContainerStatus === 'running') {
          await fetchSession();
          return;
        }

        if (nextSessionStatus && nextSessionStatus !== currentSessionStatus) {
          await fetchSession();
        }
      }
    } catch {
      // Silently ignore errors
    }
  }, [session?.id, session?.status, session?.containerId, sessionId, fetchSession, isGiftAgentSession, accessInfo]);

  useInterval(pollStatus, 30000);

  const stopSession = async () => {
    setActionLoading('stop');
    try {
      const isRunning = session?.status === 'running';
      const endpoint = isRunning
        ? `/api/sessions/${sessionId}/stop`
        : `/api/sessions/${sessionId}/restart`;
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success(isRunning ? t('session:messages.stopped') : t('session:messages.started'));
        setConfirmAction(null);
        await fetchSession();
      } else {
        const data = await response.json();
        toast.error(data.error || (isRunning ? t('session:errors.stop') : t('session:errors.start')));
      }
    } catch (error) {
      console.error('Failed to stop session:', error);
      toast.error(session?.status === 'running' ? t('session:errors.stop') : t('session:errors.start'));
    } finally {
      setActionLoading(null);
    }
  };

  const restartSession = async () => {
    setActionLoading('restart');
    try {
      const response = await fetch(`/api/sessions/${sessionId}/restart`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success(t('session:messages.restarted'));
        setConfirmAction(null);
        await fetchSession();
      } else {
        const data = await response.json();
        toast.error(data.error || t('session:errors.restart'));
      }
    } catch (error) {
      console.error('Failed to restart session:', error);
      toast.error(t('session:errors.restart'));
    } finally {
      setActionLoading(null);
    }
  };

  const recreateSession = async () => {
    setActionLoading('recreate');
    try {
      const response = await fetch(`/api/sessions/${sessionId}/recreate`, {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        let terminalStatus = '';
        for (let attempt = 0; attempt < 90; attempt += 1) {
          const statusResponse = await fetch(
            `/api/sessions/${sessionId}/system-update/status`,
            { credentials: 'include' },
          );
          const statusJson = await statusResponse.json().catch(() => ({}));
          const status = String(statusJson?.data?.status || '');
          terminalStatus = status;
          if (status === 'ready' || status === 'rolled_back' || status === 'failed') {
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
        if (terminalStatus === 'ready') {
          toast.success(t('session:messages.recreated'));
        } else if (terminalStatus === 'rolled_back') {
          toast.warning('Update failed; the previous runtime was restored.');
        } else if (terminalStatus === 'failed') {
          toast.error(t('session:errors.recreate'));
        } else {
          toast.info('Update is still running. Refresh this page to check status.');
        }
        await fetchSession();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.error || t('session:errors.recreate'));
      }
    } catch (error) {
      console.error('Failed to recreate session:', error);
      toast.error(t('session:errors.recreate'));
    } finally {
      setActionLoading(null);
    }
  };

  const deleteSession = async () => {
    setActionLoading('delete');
    try {
      const response = await fetch(`/api/sessions/${sessionId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        toast.success(t('session:messages.deleted'));
        navigate('/sessions');
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.error || t('session:errors.delete'));
        setActionLoading(null);
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast.error(t('session:errors.delete'));
      setActionLoading(null);
    }
  };

  const handleConfirm = () => {
    if (confirmAction === 'stop') stopSession();
    else if (confirmAction === 'restart') restartSession();
    else if (confirmAction === 'delete') deleteSession();
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(t('session:messages.copied'));
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const openSession = () => {
    window.open(getSessionOpenUrl(sessionId || ''), '_blank');
  };

  const formatElapsed = (date: string | Date) => {
    const now = new Date();
    const created = new Date(date);
    const diffMs = now.getTime() - created.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return t('session:elapsed.dayHour', { days: diffDays, hours: diffHours % 24 });
    if (diffHours > 0) return t('session:elapsed.hourMinute', { hours: diffHours, minutes: diffMins % 60 });
    return t('session:elapsed.minute', { minutes: diffMins });
  };

  const formatDate = (date: string | Date) => {
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-48 rounded-lg" />
        <Skeleton className="h-36 rounded-lg" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('session:notFound')}</p>
      </div>
    );
  }

  const runnerKey = RUNNER_LABEL_KEYS[session.runnerType || ''];
  const runnerLabel = runnerKey ? t(`session:runnerLabels.${runnerKey}`) : (session.runnerType || t('session:runnerLabels.unknown'));
  const statusLabel = t(`session:status.${session.status}`, { defaultValue: session.status.charAt(0).toUpperCase() + session.status.slice(1) });
  const elapsed = formatElapsed(session.createdAt);
  const runtimeInfo = accessInfo?.runtime || session.metadata || null;
  const showSystemUpdateBanner = isGiftAgentSession && runtimeInfo?.systemUpdateAvailable;
  const currentSystemVersion = runtimeInfo?.systemVersion || t('session:updateBanner.unknownVersion');
  const latestSystemVersion = runtimeInfo?.latestSystemVersion || t('session:updateBanner.unknownVersion');
  const latestSystemReleaseNotes = runtimeInfo?.latestSystemReleaseNotes || t('session:updateBanner.noUpdateNotes');

  const confirmDialogConfig = confirmAction
    ? {
      stop: {
        title: session.status === 'running' ? t('session:confirm.stopTitle') : t('session:confirm.startTitle'),
        description: session.status === 'running'
          ? t('session:confirm.stopDescription')
          : t('session:confirm.startDescription'),
        confirmLabel: session.status === 'running' ? t('session:actions.stop') : t('session:actions.start'),
        variant: 'default' as const,
      },
      restart: {
        title: t('session:confirm.restartTitle'),
        description: t('session:confirm.restartDescription'),
        confirmLabel: t('session:actions.restart'),
        variant: 'default' as const,
      },
      delete: {
        title: t('session:confirm.deleteTitle'),
        description: t('session:confirm.deleteDescription'),
        confirmLabel: t('session:actions.delete'),
        variant: 'destructive' as const,
      },
    }[confirmAction]
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('session:header.title', { name: project?.name || t('session:runnerLabels.unknown') })}
        description={t('session:header.description', { status: statusLabel, runner: runnerLabel, elapsed })}
        action={
          <div className="flex items-center gap-2">
            {session.status === 'running' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmAction('stop')}
                  disabled={actionLoading === 'stop'}
                >
                  {actionLoading === 'stop' ? <Spinner className="w-4 h-4 mr-1" /> : <Square className="w-4 h-4 mr-1" />}
                  {t('session:actions.stop')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmAction('restart')}
                  disabled={actionLoading === 'restart'}
                >
                  {actionLoading === 'restart' ? <Spinner className="w-4 h-4 mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                  {t('session:actions.restart')}
                </Button>
              </>
            )}
            {session.status === 'stopped' && session.containerId && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmAction('stop')}
                  disabled={actionLoading === 'stop'}
                >
                  {actionLoading === 'stop' ? <Spinner className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                  {t('session:actions.start')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmAction('restart')}
                  disabled={actionLoading === 'restart'}
                >
                  {actionLoading === 'restart' ? <Spinner className="w-4 h-4 mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                  {t('session:actions.restart')}
                </Button>
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirmAction('delete')}
              disabled={actionLoading === 'delete'}
              className="text-destructive hover:text-destructive"
            >
              {actionLoading === 'delete' ? <Spinner className="w-4 h-4 mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              {t('session:actions.delete')}
            </Button>
          </div>
        }
      />

      {showSystemUpdateBanner && (
        <div className="rounded-lg border bg-card px-5 py-4 text-card-foreground">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-1">
              <h2 className="text-sm font-semibold">
                {t('session:updateBanner.title')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('session:updateBanner.description')}
              </p>
              <p className="text-xs leading-5 text-muted-foreground">
                {t('session:updateBanner.version', {
                  current: currentSystemVersion,
                  latest: latestSystemVersion,
                })}
                <span className="mx-1 text-muted-foreground/60">·</span>
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 align-baseline text-xs font-semibold text-primary underline underline-offset-2"
                  onClick={() => setShowUpdateDetails(true)}
                >
                  {t('session:updateBanner.viewUpdates')}
                </Button>
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => void recreateSession()}
              disabled={actionLoading === 'recreate'}
              className="shrink-0"
            >
              {actionLoading === 'recreate' ? (
                <Spinner className="w-4 h-4 mr-1" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-1" />
              )}
              {t('session:actions.recreate')}
            </Button>
          </div>
        </div>
      )}

      {session.status === 'running' && accessInfo && (
        <div className="bg-card border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold font-heading">{t('session:access.title')}</h2>
            <Button
              size="sm"
              onClick={() => {
                if (session.runnerType === 'acpx' || session.runnerType === 'acpx-experimental') {
                  window.open(getSessionOpenUrl(session.id), '_blank');
                } else {
                  openSession();
                }
              }}
              className="gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {(session.runnerType === 'acpx' || session.runnerType === 'acpx-experimental') ? t('session:actions.openAgent') : t('session:actions.openSession')}
            </Button>
          </div>
          <div className="space-y-4">
            {accessInfo.previewUrls.port5173 && (
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground w-32 shrink-0">{t('session:access.preview')}</span>
                <span className="text-[13px] font-mono truncate flex-1">{accessInfo.previewUrls.port5173}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(accessInfo.previewUrls.port5173!, '_blank')}
                  aria-label={t('session:access.openPreview')}
                  className="shrink-0 gap-1.5 border-primary/30 text-primary hover:bg-primary/10 hover:text-primary"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  {t('session:actions.open')}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => copyToClipboard(accessInfo.previewUrls.port5173!, 'preview5173')}
                  aria-label={t('session:access.copyToClipboard')}
                  className="shrink-0"
                >
                  {copiedField === 'preview5173' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            )}

            <div className="pt-2 border-t">
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                {t('session:access.advanced')}
              </button>
              {showAdvanced && (
                <div className="space-y-4 mt-3">
                  {(() => {
                    const isAcpx = session.runnerType === 'acpx' || session.runnerType === 'acpx-experimental';
                    const sessionUrl = isAcpx ? (accessInfo.agentUrl || '') : accessInfo.codeServerUrl;
                    if (!sessionUrl) return null;
                    return (
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-muted-foreground w-32 shrink-0">{t('session:access.sessionUrl')}</span>
                        <span className="text-[13px] font-mono truncate flex-1">{sessionUrl}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyToClipboard(sessionUrl, 'url')}
                          aria-label={t('session:access.copyToClipboard')}
                          className="shrink-0"
                        >
                          {copiedField === 'url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    );
                  })()}

                  {accessInfo.codeServerPassword && (
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground w-32 shrink-0">{t('session:access.password')}</span>
                      <div className="flex-1">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          readOnly
                          value={accessInfo.codeServerPassword || ''}
                          className="text-[13px] font-mono"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? t('session:access.hidePassword') : t('session:access.showPassword')}
                        className="shrink-0"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(accessInfo.codeServerPassword || '', 'password')}
                        aria-label={t('session:access.copyToClipboard')}
                        className="shrink-0"
                      >
                        {copiedField === 'password' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  )}

                  {accessInfo.previewUrls.port3000 && (
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-muted-foreground w-32 shrink-0">{t('session:access.preview3000')}</span>
                      <span className="text-[13px] font-mono truncate flex-1">{accessInfo.previewUrls.port3000}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(accessInfo.previewUrls.port3000!, '_blank')}
                        aria-label={t('session:access.openPreview')}
                        className="shrink-0"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(accessInfo.previewUrls.port3000!, 'preview3000')}
                        aria-label={t('session:access.copyToClipboard')}
                        className="shrink-0"
                      >
                        {copiedField === 'preview3000' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="bg-card border rounded-lg p-6">
        <h2 className="text-lg font-semibold font-heading mb-4">{t('session:details.title')}</h2>
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3">
          <span className="text-sm text-muted-foreground">{t('session:details.runnerType')}</span>
          <span className="text-sm">
            <Badge variant="secondary">{runnerLabel}</Badge>
          </span>

          <span className="text-sm text-muted-foreground">{t('session:details.resources')}</span>
          <span className="text-sm">{session.cpuCores} cores / {session.memoryGb} GB</span>

          <span className="text-sm text-muted-foreground">{t('session:details.created')}</span>
          <span className="text-sm">{formatDate(session.createdAt)}</span>

          {session.containerId && (
            <>
              <span className="text-sm text-muted-foreground">{t('session:details.containerId')}</span>
              <span className="text-[13px] font-mono truncate">{session.containerId.slice(0, 12)}</span>
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmAction}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
        title={confirmDialogConfig?.title || ''}
        description={confirmDialogConfig?.description || ''}
        confirmLabel={confirmDialogConfig?.confirmLabel}
        cancelLabel={t('common:cancel')}
        variant={confirmDialogConfig?.variant}
        onConfirm={handleConfirm}
        loading={!!actionLoading}
      />
      <Dialog open={showUpdateDetails} onOpenChange={setShowUpdateDetails}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t('session:updateBanner.dialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('session:updateBanner.dialogDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-card px-3 py-2 text-xs text-card-foreground">
            {t('session:updateBanner.version', {
              current: currentSystemVersion,
              latest: latestSystemVersion,
            })}
          </div>
          <div className="whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-sm text-foreground">
            {latestSystemReleaseNotes}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
